import { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { getVzStore, fmtNum } from "@/lib/verizonSalesApi";
import { StatusChip } from "./StoreLeaderboard";
import { MonthlyHistoryChart } from "./MonthlyHistoryChart";
import { PositionBar } from "./StoreRankings";

const fmtRaw = (k, v) => {
  if (typeof v === "number" && (k.includes("%") || k.toLowerCase().includes("mix")) && Math.abs(v) <= 5) return `${(v * 100).toFixed(1)}%`;
  if (typeof v === "number" && /tsp|zip|phone|id\b/i.test(k)) return String(Math.round(v));
  if (typeof v === "number") return fmtNum(v, 2);
  return String(v);
};

export const StoreDetailSheet = ({ store, mode, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    setDetail(null); setQ("");
    if (store) getVzStore(store.door_tsp, mode).then(setDetail).catch(() => setDetail({ raw: {}, daily: [] }));
  }, [store, mode]);

  const fields = useMemo(() => Object.entries(detail?.raw || {})
    .filter(([k]) => !q || k.toLowerCase().includes(q.toLowerCase())), [detail, q]);

  return (
    <Sheet open={!!store} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="vz-store-sheet">
        {store && (
          <>
            <SheetHeader>
              <SheetTitle className="font-heading text-navy">{store.location?.name || `${store.city}, ${store.state}`}</SheetTitle>
              <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                <span>{store.store_id} · TSP {store.door_tsp}</span><StatusChip status={store.status} />
                <span>{store.address}, {store.city}, {store.state} {store.zip_code}</span>
              </div>
            </SheetHeader>
            {!detail ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div> : (
              <div className="mt-5 space-y-5">
                {store.rank?.pacing_rank && (
                  <section className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="vz-store-rank">
                    {store.overall && (
                      <div className="flex justify-between text-xs" data-testid="vz-store-overall-rank"><span className="text-slate-500">Overall Rank (avg % of goals)</span>
                        <b className="text-navy">{store.overall.rank} · {(store.overall.score * 100).toFixed(1)}%</b></div>
                    )}
                    <div className="flex justify-between text-xs"><span className="text-slate-500">Network rank (Pacing %)</span><b className="text-navy">{store.rank.pacing_rank}</b></div>
                    <PositionBar rank={store.rank.pacing_rank} total={store.rank.pacing_total} testId="vz-store-rank-bar" />
                    <div className="flex justify-between text-xs"><span className="text-slate-500">Network rank (MTD Acts)</span><b className="text-navy">{store.rank.acts_rank}</b></div>
                  </section>
                )}
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Monthly history</h4>
                  <MonthlyHistoryChart monthly={detail.monthly} compact testId="vz-store-monthly" />
                </section>
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Daily sales</h4>
                  <div className="overflow-hidden border border-slate-200 rounded-lg">
                    <table className="vz-compact w-full" data-testid="vz-store-daily-table">
                      <thead className="bg-slate-50 text-slate-500"><tr><th className="px-3 py-1.5 text-left">Date</th><th className="px-2 text-right">Acts</th><th className="px-2 text-right">Upg</th><th className="px-2 text-right">Edge</th><th className="px-3 text-right">Top-Ups</th></tr></thead>
                      <tbody>
                        {[...detail.daily].reverse().map((d) => (
                          <tr key={d.date} className="border-t border-slate-100"><td className="px-3 py-1.5">{d.date}</td><td className="px-2 text-right">{fmtNum(d.acts)}</td><td className="px-2 text-right">{fmtNum(d.upgrades)}</td><td className="px-2 text-right">{fmtNum(d.edge_apps)}</td><td className="px-3 text-right">{fmtNum(d.topups)}</td></tr>
                        ))}
                        {detail.daily.length === 0 && <tr><td colSpan={5} className="text-center py-4 text-slate-500">No daily data</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </section>
                <section>
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">All DLAR fields ({fields.length})</h4>
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter fields…" className="h-7 w-40 text-xs" data-testid="vz-store-field-filter" />
                  </div>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs" data-testid="vz-store-fields">
                    {fields.map(([k, v]) => (
                      <div key={k} className="flex justify-between border-b border-slate-100 py-1 gap-2">
                        <dt className="text-slate-500 truncate">{k}</dt><dd className="font-medium text-navy text-right truncate">{fmtRaw(k, v)}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
